/**
 * Forgot password page — public, no auth required.
 *
 * Submits the user's email to the forgot-password endpoint, which always
 * returns the same message to prevent user enumeration.
 */

import { useState, type FormEvent } from 'react';
import BrandTitle from '@/components/common/BrandTitle';
import BrandMark from '@/components/common/BrandMark';
import { Link } from 'react-router-dom';
import { forgotPassword } from '@/api/users';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Brand */}
        <div className="text-center">
          <BrandMark className="mx-auto h-14 w-14" />
          <BrandTitle/>
          <p className="mt-1 text-sm text-gray-500">Reset your password</p>
        </div>

        {submitted ? (
          <div className="space-y-4 text-center">
            <div className="rounded-md bg-green-50 px-4 py-4">
              <p className="text-sm font-medium text-green-800">Check your email</p>
              <p className="mt-1 text-sm text-green-700">
                If an account with that email exists, a reset link has been sent.
              </p>
            </div>
            <Link
              to="/login"
              className="block text-sm text-indigo-600 hover:text-indigo-800"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="input-field mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary w-full">
              {isLoading ? 'Sending…' : 'Send Reset Link'}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
